import Papa from 'papaparse'
import { z } from 'zod'
import { apiRequest } from './client'

// ── Wire schemas (camelCase, matching the FastAPI response) ──────────────────

export const LeaveRowResultSchema = z.object({
  rowIndex: z.number().int(),
  employeeNo: z.string(),
  csvEmployeeName: z.string(),
  matchedEmployeeName: z.string().nullable(),
  matchedEmployeeEmail: z.string().nullable(),
  leaveType: z.string().nullable(),
  action: z.enum(['create', 'update', 'skip', 'error']),
  entitlement: z.string().nullable(),
  used: z.string().nullable(),
  csvBalance: z.string().nullable(),
  lmsComputedAvailable: z.string().nullable(),
  discrepancy: z.boolean(),
  discrepancyReason: z.string().nullable(),
  nameMismatch: z.boolean(),
  error: z.string().nullable(),
  note: z.string().nullable(),
})
export type LeaveRowResult = z.infer<typeof LeaveRowResultSchema>

export const LeaveImportResultSchema = z.object({
  dryRun: z.boolean(),
  total: z.number().int(),
  wouldCreate: z.number().int(),
  wouldUpdate: z.number().int(),
  skipped: z.number().int(),
  errorCount: z.number().int(),
  discrepancyCount: z.number().int(),
  rows: z.array(LeaveRowResultSchema),
})
export type LeaveImportResult = z.infer<typeof LeaveImportResultSchema>

// A row as posted to the API (strings preserve exact decimals like "3.50").
export interface LeaveImportRow {
  year: number
  employeeNo: string
  employeeName: string
  leaveType: string
  entitlement: string
  /** Taken/Applied/csvBalance are optional. When Taken AND Applied are both
   *  omitted the server treats the row as "entitlement-only": it rewrites
   *  entitlement (+ the annual grant from Yearly) but preserves the live
   *  used/pending. Present → full old-system migration path. */
  taken?: string
  applied?: string
  openingBalance: string
  adjustment: string
  /** CSV "Yearly" — full annual grant. Drives employees.annual_entitlement for
   *  annual rows server-side. Falls back to "Current" when the column is absent. */
  yearly: string
  csvBalance?: string
}

// ── CSV parsing (old-system export header) ───────────────────────────────────

// Taken/Applied/Balance are intentionally NOT required: a CSV with just the
// entitlement column drives the entitlement-only path, which leaves the live
// used/pending untouched.
const REQUIRED_HEADERS = ['year', 'employee no.', 'employee name', 'leave type'] as const

// The entitlement to set can come from any one of these headers. "Entitlement"
// is the current name; "Current"/"Yearly" are accepted for old-system exports
// (where Current is the year slice and Yearly the full grant — see parse below).
const ENTITLEMENT_HEADERS = ['entitlement', 'yearly', 'current'] as const

export function parseLeaveCsv(csv: string): {
  rows: LeaveImportRow[]
  errors: Array<{ line: number; message: string }>
} {
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase(),
  })
  const errors: Array<{ line: number; message: string }> = []
  const fields: string[] = Array.isArray(parsed.meta.fields) ? parsed.meta.fields : []
  const present = new Set(fields)
  const missing = REQUIRED_HEADERS.filter((h) => !present.has(h))
  const hasEntitlement = ENTITLEMENT_HEADERS.some((h) => present.has(h))
  if (missing.length > 0 || !hasEntitlement) {
    const parts: string[] = []
    if (missing.length > 0) parts.push(`Missing required column(s): ${missing.join(', ')}`)
    if (!hasEntitlement) parts.push('Missing an entitlement column — add an "Entitlement" column')
    return { rows: [], errors: [{ line: 0, message: parts.join('. ') }] }
  }

  const rows: LeaveImportRow[] = []
  parsed.data.forEach((raw, i) => {
    const line = i + 2 // header is line 1
    const yearStr = (raw['year'] ?? '').trim()
    const employeeNo = (raw['employee no.'] ?? '').trim()
    const year = Number.parseInt(yearStr, 10)
    if (!yearStr || Number.isNaN(year)) {
      errors.push({ line, message: `Row ${line}: Year is blank — fill it in before importing` })
      return
    }
    if (!employeeNo) {
      errors.push({ line, message: `Row ${line}: Employee No. is blank` })
      return
    }
    const row: LeaveImportRow = {
      year,
      employeeNo,
      employeeName: (raw['employee name'] ?? '').trim(),
      leaveType: (raw['leave type'] ?? '').trim(),
      // entitlement (the year slice) prefers the plain "Entitlement" column,
      // then the old-system "Current"; yearly (the full grant) prefers
      // "Yearly", else falls back to the same entitlement value. So a single
      // "Entitlement" column sets both the year slice and the grant.
      entitlement: (raw['entitlement'] ?? raw['current'] ?? raw['yearly'] ?? '0').trim(),
      openingBalance: (raw['opening balance (bf)'] ?? '0').trim(),
      adjustment: (raw['adjustment'] ?? '0').trim(),
      yearly: (raw['yearly'] ?? raw['entitlement'] ?? raw['current'] ?? '0').trim(),
    }
    // Only carry Taken/Applied/Balance when their column was actually present,
    // so an entitlement-only CSV posts them as undefined (server preserves
    // live used/pending) rather than a defaulted "0" (which would zero them).
    if (present.has('taken')) row.taken = (raw['taken'] ?? '0').trim()
    if (present.has('applied')) row.applied = (raw['applied'] ?? '0').trim()
    if (present.has('balance')) row.csvBalance = (raw['balance'] ?? '0').trim()
    rows.push(row)
  })
  return { rows, errors }
}

export async function importLeaveBalances(
  rows: LeaveImportRow[],
  dryRun: boolean,
): Promise<LeaveImportResult> {
  const raw = await apiRequest<unknown>('/api/v1/hr/leave-balances/import', {
    method: 'POST',
    body: { rows, dryRun },
  })
  return LeaveImportResultSchema.parse(raw)
}

// ── Downloadable template ────────────────────────────────────────────────────

export const LEAVE_IMPORT_TEMPLATE_CSV = [
  'Year,Employee No.,Employee Name,Leave Type,Opening Balance (BF),Yearly,Current,Taken,Applied,Adjustment,Balance',
  '2026,03730,JANE DOE,ANNUAL LEAVE,0.00,16.00,16.00,4.00,0.00,0.00,12.00',
].join('\n')

// Entitlement-only template: no Taken/Applied/Balance columns. Sets each
// employee's entitlement (+ annual grant from Yearly) while leaving the days
// they've already taken and any pending requests exactly as the system tracks
// them. Use this for a launch-time adjustment like "everyone gets 20".
export const LEAVE_IMPORT_ENTITLEMENT_ONLY_TEMPLATE_CSV = [
  'Year,Employee No.,Employee Name,Leave Type,Entitlement',
  '2026,03730,JANE DOE,ANNUAL LEAVE,20.00',
].join('\n')
